import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import {
	ArrowUp,
	ArrowDown,
	Search,
	RefreshCcw,
	ChevronDown,
	Calendar,
	X,
	MessageCircle,
	CheckCircle,
	Edit3,
	Truck,
	FileText,
	Share2,
	Activity,
	Shield,
	Mail
} from 'react-feather';
import { DataTable } from '@/stories/Shadcn/DataTable/DataTable';
import { useCustomerStore } from './useCustomers';
import { FincluCustomer, CustomerProcessStatus } from '@/pages/CustomerSupport/types';
import Button from '@/stories/Library/Components/Button/Button';
import { getLanguageFromPath, formatDateTime } from '@/utils/utils';
import { DEFAULT_LANGUAGE } from '@/constants/languages';
import { Typography } from '@/UIlibrary/Typography/Typography';
import InputField from '@/UIlibrary/InputField/InputField';
import { useTranslations } from 'use-intl';
import { toTranslationKey } from '@/utils/translations';
import { Roles } from '@/pages/ClientProfiles/Roles.types';
import { useSupabaseAuth } from '@/pages/CustomerSupport/useSupabaseAuth';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { es } from 'date-fns/locale';
import { BRAND_COLORS } from '@/UIlibrary/globalTheme';
import { FincluOperationStatus } from '@/types/FincluOperationStatus';
import { FincluStatusBadge } from './FincluStatusBadge';
import { FincluStatusFilter } from './FincluStatusFilter';
import { TextBadge } from '@/UIlibrary/TextBadge/TextBadge';

const statusLabels: Record<CustomerProcessStatus, string> = {
	[CustomerProcessStatus.AGENT_CUSTOMER_SUPPORT]: 'StatusAgentCustomerSupport',
	[CustomerProcessStatus.SENT_APPROVED_MESSAGE]: 'StatusSentApprovedMessage',
	[CustomerProcessStatus.AGENT_FILLING_QUESTIONNARE]: 'StatusAgentFillingQuestionnare',
	[CustomerProcessStatus.AGENT_SUGGESTING_VEHICLE]: 'StatusAgentSuggestingVehicle',
	[CustomerProcessStatus.AGENT_GATHERING_DOCUMENTS]: 'StatusAgentGatheringDocuments',
	[CustomerProcessStatus.AGENT_GATHERING_SOCIAL_NETWORKS]: 'StatusAgentGatheringSocialNetworks',
	[CustomerProcessStatus.AGENT_ANALYZING_SOCIAL_NETWORKS]: 'StatusAgentAnalyzingSocialNetworks',
	[CustomerProcessStatus.AGENT_RISK_CALCULATION]: 'StatusAgentRiskCalculation'
};

const statusIcons: Record<CustomerProcessStatus, React.ReactNode> = {
	[CustomerProcessStatus.AGENT_CUSTOMER_SUPPORT]: <MessageCircle size={16} />,
	[CustomerProcessStatus.SENT_APPROVED_MESSAGE]: <CheckCircle size={16} />,
	[CustomerProcessStatus.AGENT_FILLING_QUESTIONNARE]: <Edit3 size={16} />,
	[CustomerProcessStatus.AGENT_SUGGESTING_VEHICLE]: <Truck size={16} />,
	[CustomerProcessStatus.AGENT_GATHERING_DOCUMENTS]: <FileText size={16} />,
	[CustomerProcessStatus.AGENT_GATHERING_SOCIAL_NETWORKS]: <Share2 size={16} />,
	[CustomerProcessStatus.AGENT_ANALYZING_SOCIAL_NETWORKS]: <Activity size={16} />,
	[CustomerProcessStatus.AGENT_RISK_CALCULATION]: <Shield size={16} />
};

const statusColors: Record<CustomerProcessStatus, string> = {
	[CustomerProcessStatus.AGENT_CUSTOMER_SUPPORT]: '#3B82F6',
	[CustomerProcessStatus.SENT_APPROVED_MESSAGE]: '#10B981',
	[CustomerProcessStatus.AGENT_FILLING_QUESTIONNARE]: '#8B5CF6',
	[CustomerProcessStatus.AGENT_SUGGESTING_VEHICLE]: '#F59E0B',
	[CustomerProcessStatus.AGENT_GATHERING_DOCUMENTS]: '#EF4444',
	[CustomerProcessStatus.AGENT_GATHERING_SOCIAL_NETWORKS]: '#06B6D4',
	[CustomerProcessStatus.AGENT_ANALYZING_SOCIAL_NETWORKS]: '#EC4899',
	[CustomerProcessStatus.AGENT_RISK_CALCULATION]: '#6366F1'
};

const SupportClientsPage: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const language = getLanguageFromPath(window.location.pathname) || DEFAULT_LANGUAGE;
	const t = useTranslations('SupportClients');
	const { isLoading: authLoading, isKogniUser } = useSupabaseAuth(
		`${Roles.CUSTOMER_SUPPORT},${Roles.KOGNI_ADMIN}`
	);
	const {
		customers,
		isLoading,
		totalCount,
		hasMore,
		filters,
		sorting,
		loadCustomers,
		loadMoreCustomers,
		setFilters,
		setSorting
	} = useCustomerStore();

	const [searchInput, setSearchInput] = useState(filters.searchTerm || '');
	const isInitialMount = useRef(true);
	const [selectedStatuses, setSelectedStatuses] = useState<CustomerProcessStatus[]>(
		filters.status || []
	);
	const [selectedFitForCredit, setSelectedFitForCredit] = useState<boolean | null | undefined>(
		filters.fitForVehicularCredit
	);
	const [selectedFincluStatuses, setSelectedFincluStatuses] = useState<FincluOperationStatus[]>(
		(filters.fincluStatuses as FincluOperationStatus[]) || []
	);
	const [showDatePicker, setShowDatePicker] = useState(false);
	const datePickerRef = useRef<HTMLDivElement>(null);
	const [dateRange, setDateRange] = useState<{
		startDate: Date | undefined;
		endDate: Date | undefined;
		key: string;
	}>({
		startDate: filters.dateFrom ? new Date(filters.dateFrom) : undefined,
		endDate: filters.dateTo ? new Date(filters.dateTo) : undefined,
		key: 'selection'
	});

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
				setShowDatePicker(false);
			}
		};

		if (showDatePicker) {
			document.addEventListener('mousedown', handleClickOutside);
			return () => document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [showDatePicker]);

	const isDetailView =
		location.pathname.includes('/support-clients/') &&
		location.pathname !== `/${language}/center/support-clients`;

	useEffect(() => {
		if (!authLoading) {
			loadCustomers();
		}
	}, [authLoading, loadCustomers]);

	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		const timeoutId = setTimeout(() => {
			const currentFilters = useCustomerStore.getState().filters;
			setFilters({ ...currentFilters, searchTerm: searchInput });
			loadCustomers();
		}, 800);

		return () => clearTimeout(timeoutId);
	}, [searchInput, loadCustomers, setFilters]);

	const handleStatusFilter = (status: CustomerProcessStatus) => {
		const newStatuses = selectedStatuses.includes(status)
			? selectedStatuses.filter((s) => s !== status)
			: [...selectedStatuses, status];
		setSelectedStatuses(newStatuses);
		setFilters({ ...filters, status: newStatuses });
		loadCustomers();
	};

	const handleDateRangeChange = (
		rangesByKey: Record<string, { startDate?: Date; endDate?: Date }>
	) => {
		const selection = rangesByKey.selection;
		if (selection?.startDate && selection?.endDate) {
			setDateRange({
				startDate: selection.startDate,
				endDate: selection.endDate,
				key: 'selection'
			});
			setShowDatePicker(false);
			const startDateStr = new Date(selection.startDate);
			startDateStr.setHours(0, 0, 0, 0);
			const endDateStr = new Date(selection.endDate);
			endDateStr.setHours(23, 59, 59, 999);
			setFilters({
				...filters,
				dateFrom: startDateStr.toISOString(),
				dateTo: endDateStr.toISOString()
			});
			loadCustomers();
		}
	};

	const handleClearDateRange = () => {
		setDateRange({
			startDate: undefined,
			endDate: undefined,
			key: 'selection'
		});
		setFilters({
			...filters,
			dateFrom: undefined,
			dateTo: undefined
		});
		loadCustomers();
	};

	const handleFitForCreditFilter = (value: boolean | null | undefined) => {
		const newValue = selectedFitForCredit === value ? undefined : value;
		setSelectedFitForCredit(newValue);
		setFilters({ ...filters, fitForVehicularCredit: newValue });
		loadCustomers();
	};

	const handleFincluStatusChange = (statuses: FincluOperationStatus[]) => {
		setSelectedFincluStatuses(statuses);
		setFilters({ ...filters, fincluStatuses: statuses as string[] });
		loadCustomers();
	};

	const handleClearFilters = () => {
		setSearchInput('');
		setSelectedStatuses([]);
		setSelectedFitForCredit(undefined);
		setSelectedFincluStatuses([]);
		setDateRange({
			startDate: undefined,
			endDate: undefined,
			key: 'selection'
		});
		setFilters({});
		loadCustomers();
	};

	const formatDate = (dateString: string) => {
		const locale = language === 'es' ? 'es-ES' : 'en-US';
		return new Date(dateString).toLocaleDateString(locale, {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		});
	};

	const formatPhoneNumber = (phone: string) => {
		const cleaned = phone.replace(/\D/g, '');
		if (cleaned.length === 10) {
			return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
		}
		return phone;
	};

	const StatusFilter = () => {
		const [isOpen, setIsOpen] = useState(false);
		const dropdownRef = useRef<HTMLDivElement>(null);

		useEffect(() => {
			const handleClickOutside = (event: MouseEvent) => {
				if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
					setIsOpen(false);
				}
			};

			document.addEventListener('mousedown', handleClickOutside);
			return () => document.removeEventListener('mousedown', handleClickOutside);
		}, []);

		return (
			<div className="relative" ref={dropdownRef}>
				<button
					onClick={() => setIsOpen(!isOpen)}
					className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 bg-white border border-gray-300 rounded px-2 py-1"
				>
					<span>
						{selectedStatuses.length > 0 ? `${selectedStatuses.length} ${t('Selected')}` : t('All')}
					</span>
					<ChevronDown size={12} />
				</button>
				{isOpen && (
					<div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-50 min-w-[200px]">
						{Object.values(CustomerProcessStatus).map((status) => (
							<label
								key={status}
								className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
							>
								<input
									type="checkbox"
									checked={selectedStatuses.includes(status)}
									onChange={() => handleStatusFilter(status)}
									className="cursor-pointer"
								/>
								<span>{t(toTranslationKey(statusLabels[status]))}</span>
							</label>
						))}
					</div>
				)}
			</div>
		);
	};

	const FitForCreditFilter = () => {
		const [isOpen, setIsOpen] = useState(false);
		const dropdownRef = useRef<HTMLDivElement>(null);

		useEffect(() => {
			const handleClickOutside = (event: MouseEvent) => {
				if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
					setIsOpen(false);
				}
			};

			document.addEventListener('mousedown', handleClickOutside);
			return () => document.removeEventListener('mousedown', handleClickOutside);
		}, []);

		const getFilterLabel = () => {
			if (selectedFitForCredit === true) return t('Apt');
			if (selectedFitForCredit === false) return t('NotApt');
			if (selectedFitForCredit === null) return t('Analyzing');
			return t('All');
		};

		return (
			<div className="relative" ref={dropdownRef}>
				<button
					onClick={() => setIsOpen(!isOpen)}
					className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 bg-white border border-gray-300 rounded px-2 py-1"
				>
					<span>{getFilterLabel()}</span>
					<ChevronDown size={12} />
				</button>
				{isOpen && (
					<div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-50 min-w-[150px]">
						<label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
							<input
								type="radio"
								checked={selectedFitForCredit === undefined}
								onChange={() => handleFitForCreditFilter(undefined as any)}
								className="cursor-pointer"
							/>
							<span>{t('All')}</span>
						</label>
						<label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
							<input
								type="radio"
								checked={selectedFitForCredit === true}
								onChange={() => handleFitForCreditFilter(true)}
								className="cursor-pointer"
							/>
							<span className="text-green-600">{t('Apt')}</span>
						</label>
						<label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
							<input
								type="radio"
								checked={selectedFitForCredit === false}
								onChange={() => handleFitForCreditFilter(false)}
								className="cursor-pointer"
							/>
							<span className="text-red-600">{t('NotApt')}</span>
						</label>
						<label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
							<input
								type="radio"
								checked={selectedFitForCredit === null}
								onChange={() => handleFitForCreditFilter(null)}
								className="cursor-pointer"
							/>
							<span className="text-blue-600">{t('Analyzing')}</span>
						</label>
					</div>
				)}
			</div>
		);
	};

	const handleColumnSort = (columnKey: string) => {
		const currentSort = sorting || { column: 'created_at', direction: 'asc' };
		const newDirection =
			currentSort.column === columnKey && currentSort.direction === 'asc' ? 'desc' : 'asc';
		setSorting({ column: columnKey, direction: newDirection });
		loadCustomers();
	};

	const getSortIcon = (columnKey: string) => {
		const currentSort = sorting || { column: 'created_at', direction: 'asc' };
		if (currentSort.column !== columnKey) return null;
		return currentSort.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
	};

	const columns: ColumnDef<FincluCustomer>[] = [
		{
			accessorKey: 'names',
			header: () => (
				<div
					className="cursor-pointer flex items-center gap-2"
					onClick={() => handleColumnSort('names')}
				>
					{t('Name')}
					{getSortIcon('names')}
				</div>
			),
			cell: ({ row }) => {
				const names = row.original.names || '';
				const firstLast = row.original.first_last_name || '';
				const secondLast = row.original.second_last_name || '';
				const fullName = `${names} ${firstLast} ${secondLast}`.trim() || 'N/A';
				const email = row.original.email || '';

				return (
					<div className="flex items-center gap-2">
						<span>{fullName}</span>
						<div className="inline-flex items-center relative group">
							{email && (
								<span className="inline-flex items-center text-gray-500 hover:text-gray-700 transition-colors cursor-pointer">
									<Mail size={14} />
								</span>
							)}
							<span className="absolute left-1/2 -translate-x-1/2 -top-8 text-xs bg-gray-800 text-white px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 shadow-md z-50">
								{email}
							</span>
						</div>
					</div>
				);
			}
		},
		{
			accessorKey: 'phone_number',
			header: () => (
				<div
					className="cursor-pointer flex items-center gap-2"
					onClick={() => handleColumnSort('phone_number')}
				>
					{t('Phone')}
					{getSortIcon('phone_number')}
				</div>
			),
			cell: ({ row }) => formatPhoneNumber(row.original.phone_number)
		},
		{
			accessorKey: 'customer_process_status',
			header: () => (
				<div
					className="cursor-pointer flex items-center gap-2"
					onClick={() => handleColumnSort('customer_process_status')}
				>
					{t('AgentStatus')}
					{getSortIcon('customer_process_status')}
				</div>
			),
			cell: ({ row }) => {
				const status = row.original.customer_process_status;
				const icon = statusIcons[status];
				const fitForCredit = row.original.fit_for_vehicular_credit;
				const showBadge = fitForCredit !== null && fitForCredit !== undefined;

				const renderFitForCreditBadge = () => {
					if (!showBadge) {
						return (
							<span className="text-sm font-medium" style={{ color: statusColors[status] }}>
								{t(toTranslationKey(statusLabels[status]))}
							</span>
						);
					}
					return (
						<TextBadge
							text={fitForCredit ? t('Apt') : t('NotApt')}
							size="sm"
							type={fitForCredit ? 'success' : 'error'}
						/>
					);
				};

				return (
					<div className="flex items-center gap-2">
						{icon && (
							<span
								className="inline-flex items-center transition-colors cursor-pointer"
								style={{
									color: showBadge ? '#6B7280' : statusColors[status]
								}}
							>
								{icon}
							</span>
						)}
						{renderFitForCreditBadge()}
					</div>
				);
			}
		},
		{
			accessorKey: 'created_at',
			header: () => (
				<div
					className="cursor-pointer flex items-center gap-2"
					onClick={() => handleColumnSort('created_at')}
				>
					{t('Created')}
					{getSortIcon('created_at')}
				</div>
			),
			cell: ({ row }) => formatDate(row.original.created_at)
		},
		{
			accessorKey: 'finclu_operation_status',
			header: () => (
				<div
					className="cursor-pointer flex items-center gap-2"
					onClick={() => handleColumnSort('finclu_operation_status')}
				>
					{t('FincluStatus')}
					{getSortIcon('finclu_operation_status')}
				</div>
			),
			cell: ({ row }) => {
				const status = row.original.finclu_operation_status;
				if (!status) {
					return <span className="text-gray-400 text-xs">—</span>;
				}
				return <FincluStatusBadge status={status as FincluOperationStatus} size="sm" />;
			}
		}
	];

	const hideSyncClientsButton = !isKogniUser;

	if (isDetailView) {
		return <Outlet />;
	}

	return (
		<div className="p-3 md:p-8">
			<div className="mb-6 flex flex-col md:flex-row w-full justify-between items-center">
				<div className="flex flex-col gap-2">
					<Typography type="display" size="md">
						{t('Title')}
					</Typography>
					<Typography size="lg" color="secondary" weight="regular">
						{t('Description')}
					</Typography>
				</div>
				<div className={hideSyncClientsButton ? 'hidden' : ''}>
					<Button
						label={t('SyncClients')}
						onClick={() => navigate(`/${language}/center/sync-clients`)}
						color="primary"
						size="auto"
						startIcon={<RefreshCcw size={16} />}
					/>
				</div>
			</div>

			<div className="flex gap-3 mb-4 flex-wrap items-center">
				<div className="relative flex items-center" ref={datePickerRef}>
					<Button
						onClick={() => setShowDatePicker(!showDatePicker)}
						size="auto"
						endIcon={<Calendar size={20} />}
						label={
							dateRange.startDate && dateRange.endDate
								? `${formatDateTime(dateRange.startDate.toISOString()).split(' ')[0]} - ${formatDateTime(dateRange.endDate.toISOString()).split(' ')[0]}`
								: t('SelectDateRange')
						}
						color="secondary"
						outlined
					/>
					{dateRange.startDate && (
						<button
							onClick={handleClearDateRange}
							className="ml-2 text-gray-500 hover:text-gray-700"
							title={t('ClearDates')}
						>
							<X size={16} />
						</button>
					)}
					{showDatePicker && (
						<div className="absolute top-full left-0 mt-2 bg-white shadow-lg rounded-lg z-50">
							<DateRange
								showMonthAndYearPickers={true}
								showDateDisplay={false}
								ranges={[dateRange]}
								months={1}
								onChange={handleDateRangeChange}
								locale={es}
								moveRangeOnFirstSelection={false}
								direction="horizontal"
								rangeColors={[BRAND_COLORS.brand500]}
								minDate={new Date(2020, 0, 1)}
								maxDate={new Date()}
							/>
						</div>
					)}
				</div>

				<div className="flex-1 max-w-md">
					<InputField
						placeholder={t('SearchPlaceholder')}
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						type="text"
						size="auto"
						endAction={<Search size={16} className="text-gray-400" />}
					/>
				</div>
			</div>

			<div className="flex gap-3 mb-6 flex-wrap items-center">
				<div className="flex items-center gap-2">
					<span className="text-sm text-gray-600">{t('AgentStatus')}:</span>
					<StatusFilter />
				</div>

				<div className="flex items-center gap-2">
					<span className="text-sm text-gray-600">{t('FincluStatus')}:</span>
					<FincluStatusFilter
						selectedStatuses={selectedFincluStatuses}
						onStatusChange={handleFincluStatusChange}
					/>
				</div>

				<div className="flex items-center gap-2">
					<span className="text-sm text-gray-600">{t('FitForVehicularCredit')}:</span>
					<FitForCreditFilter />
				</div>

				{(searchInput ||
					selectedStatuses.length > 0 ||
					selectedFincluStatuses.length > 0 ||
					selectedFitForCredit !== undefined ||
					dateRange.startDate) && (
					<button
						onClick={handleClearFilters}
						className="text-blue-500 text-sm font-medium cursor-pointer hover:underline ml-auto"
					>
						{t('ClearFilters')}
					</button>
				)}
			</div>

			{authLoading || (isLoading && customers.length === 0) ? (
				<div className="text-center py-12">
					<p>{t('LoadingCustomers')}</p>
				</div>
			) : (
				<>
					<DataTable
						columns={columns}
						data={customers}
						onRowClick={(customer) =>
							navigate(`/${language}/center/support-clients/${customer.id}`)
						}
						enablePagination={false}
					/>

					{hasMore && (
						<div className="text-center py-4">
							<Button
								onClick={() => loadMoreCustomers()}
								size="auto"
								label={isLoading ? t('Loading') : t('LoadMore')}
								disabled={isLoading}
								color="secondary"
								endIcon={isLoading ? <RefreshCcw size={14} className="animate-spin" /> : undefined}
							/>
						</div>
					)}

					{!isLoading && customers.length === 0 && (
						<div className="text-center py-4 text-gray-500">{t('NoResults')}</div>
					)}

					{totalCount > 0 && (
						<div className="mt-1 text-center py-3 text-sm text-gray-500">
							{t('ShowingResults', {
								from: 1,
								to: customers.length,
								total: totalCount
							})}
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default SupportClientsPage;
